import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { ILoggerOptions } from 'library/ts/utilities/logger/interfaces/ILoggerOptions';
import { Logger } from 'library/ts/utilities/logger/Logger';
import { LogLevels, LogMethods, LogMethodsLevelsMap } from 'library/ts/utilities/logger/LogServiceConstants';
import { ISmartTagRuntimeVO } from 'tag/ts/smart_tag/interfaces/ISmartTagRuntimeVO';
import { getSmartTagRuntimeVO } from 'tag/ts/smart_tag/utilities/Runtime';

/**
 * @copyright © Paramount Streaming
 * @author  Jeff Appis
 * @description LogService handles the creation of Loggers and contains several
 * properties and methods which are used by instances of Logger.
 */
export class LogService {

	/**
	 * @description Returns reference to global Utilities
	 * @returns {Utilities | undefined}
	 */
	static get utils() {
		return window?.PARA?.PlayerServices?.Utilities;
	}

	/**
	 * Stores the timestamp of first load.
	 * @type {number}
	 * @private
	 */
	static #timeStart: number = Date.now();
	static get timeStart(): number {
		return window.PARA?.PlayerServices?.StartTime || LogService.#timeStart;
	}

	/**
	 * Provides the number of milliseconds since first load.
	 * @returns {number}
	 */
	static get timeElapsed(): number {
		return Date.now() - LogService.timeStart;
	}

	static #logLevel: number;
	/**
	 * The Log Level is a number representing specificity that determines which
	 * log messages will be recorded and shown.  Each Log Method is mapped to a
	 * maximum Log Level.  If the current Log Level is HIGHER then the method's
	 * Log Level then that method will not log anything.  Log Level starts at 1
	 * (debug=1) and increases up to 9 (debug=9).
	 * @type number
	 */
	static get logLevel(): number {
		if ( !LogService?.utils?.Type?.isUndefined( LogService.#logLevel ) ) {
			return LogService.#logLevel;
		}

		// check runtimeVO first (checks search + cookie)
		if ( LogService.runtimeVOLogLevel ) {
			return LogService.#logLevel = +LogService.runtimeVOLogLevel;
		}

		// set default if nothing specified
		return LogService.#logLevel = LogLevels.ERRORS_AND_INFO;
	}

	static #supportedMethods: string[];
	/**
	 * Stores an array of all supported Log Methods.  Log Methods are considered
	 * "supported" if they are implemented in at least one Log Adapter.
	 * @type {string[]}
	 */
	static get supportedMethods(): string[] {
		if ( LogService?.utils?.Type?.isUndefined( LogService.#supportedMethods ) ) {
			LogService.#supportedMethods = Object.keys( LogMethods ).map( ( methodKey ) => {
				return LogMethods[ methodKey ];
			} );
		}
		return LogService.#supportedMethods;
	}

	static #availableMethods: string[];
	/**
	 * Stores an array of all available Log Methods.  Log Methods are considered
	 * "available" if they are implemented in at least one Log Adapter AND their
	 * associated Log Level >= the current Log Level.
	 * @type {string[]}
	 */
	static get availableMethods(): string[] {
		if ( LogService?.utils?.Type?.isUndefined( LogService.#availableMethods ) ) {
			const currentLogLevel = LogService.logLevel;
			LogService.#availableMethods = LogService.supportedMethods.filter( ( logMethod ) => {
				const methodLevel = LogMethodsLevelsMap?.[ logMethod ];
				return methodLevel >= currentLogLevel;
			} );
		}
		return LogService.#availableMethods;
	}

	static methodIsAvailable( methodName: LogMethods ): boolean {
		return LogService.availableMethods.includes( methodName );
	}

	static #runtimeVOLogLevel: ISmartTagRuntimeVO;
	static get runtimeVOLogLevel(): ISmartTagRuntimeVO {
		return LogService.#runtimeVOLogLevel = LogService.#runtimeVOLogLevel || getSmartTagRuntimeVO()?.debug?.logLevel;
	}

	/**
	 * Yields a blank function, used as a substitute return value when attempting
	 * to access a Log Method that is not available.
	 * @returns {() => void}
	 */
	static get noOp(): () => void {
		return () => {
			// this function intentionally left blank
		};
	}

	/**
	 * Create a new logger instance with the supplied options parameter.
	 * @param {ILoggerOptions} options
	 * @returns {ILogger}
	 */
	static create = ( options: ILoggerOptions = {} ): ILogger => {
		return new Logger( {
			getTimeElapsed: () => LogService.timeElapsed,
			methodIsAvailable: ( method ) => LogService.methodIsAvailable( method )
		}, options );
	};
}